import React, {
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react"
import { useKeyPress } from "ahooks"
import anime from "animejs"
import classNames from "classnames"
import { motion } from "motion/react"

import { useTranslation } from "@/locales"
import Exit from "../../components/Exit"
import Nav from "../../components/Nav"
import { ESubPage, GlobalContext, ISubPageProps } from "../../context"
import { music } from "../../utils/music"
import Cover from "./Cover"
import Curtain from "./Curtain"
import Railing from "./Railing"
import Ticket from "./Ticket"
import Tree from "./Tree"

import styles from "./index.module.less"

const lastPvSrc = ""

const PagePV: React.FC<ISubPageProps> = (props) => {
    const { className, style, onExit } = props
    const {
        state: { bgm },
    } = useContext(GlobalContext)

    const { t } = useTranslation()

    const [activeIndex, setActiveIndex] = useState(1)
    const [activePvIndex, setActivePvIndex] = useState<number | null>(null)

    const coversRef = useRef<HTMLDivElement>(null)
    useEffect(() => {
        const animeInst = anime({
            targets: coversRef.current,
            translateX: `-${(activeIndex - 1) * 33.3333}%`,
            duration: 1500,
            easing: "easeOutElastic",
        })

        return () => {
            animeInst.pause()
        }
    }, [activeIndex])

    const [showVideo, setShowVideo] = useState(false)
    const videoRef = useRef<HTMLVideoElement>(null)
    const handleOpenPv = useCallback(
        (src: string, index: number) => {
            if (index === 2) {
                window.open(
                    "https://x.com/ArknightsEN/status/2000815782671053308",
                    "_blank"
                )
                return
            }
            if (bgm) {
                music.disable()
            }
            setActivePvIndex(index)
            setShowVideo(true)
        },
        [bgm]
    )

    const closePv = useCallback(async () => {
        if (bgm) {
            music.enable()
        }

        setShowVideo(false)
        if (videoRef.current) {
            const videoElement = videoRef.current
            videoElement.pause()
        }
    }, [bgm])

    const handleClosePv = useCallback(
        async (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            const target = e.target as HTMLDivElement
            if (target.classList.contains(styles.player)) {
                closePv()
            }
        },
        [closePv]
    )

    useKeyPress("esc", () => {
        if (showVideo) {
            closePv()
        } else {
            onExit?.()
        }
    })

    return (
        <motion.div
            className={classNames(styles.page, className)}
            style={style}
            initial={{
                opacity: 0,
                pointerEvents: "none",
            }}
            animate={{
                opacity: 1,
                pointerEvents: "auto",
            }}
            exit={{
                opacity: 0,
                pointerEvents: "none",
            }}
            transition={{
                duration: 0.5,
                ease: "easeInOut",
            }}
        >
            <div className={styles.cloud} />
            <motion.div
                initial={{
                    transform: "scale(1.1)",
                    opacity: 0,
                    pointerEvents: "none",
                }}
                animate={{
                    transform: "scale(1)",
                    opacity: 1,
                    pointerEvents: "auto",
                }}
                transition={{
                    duration: 0.5,
                    delay: 0.3,
                    ease: "easeInOut",
                }}
                className={styles.covers}
            >
                <div
                    ref={coversRef}
                    className={styles.wrapper}
                    data-index={activeIndex}
                >
                    {Array.from({ length: 3 }).map((_, index) => (
                        <div key={index} className={styles.item}>
                            <Cover
                                index={index + 1}
                                onClick={() =>
                                    handleOpenPv(
                                        t(`pv.${index + 1}.src`),
                                        index + 1
                                    )
                                }
                            />
                        </div>
                    ))}
                </div>
            </motion.div>
            <Tree position="left" />
            <Tree position="right" />
            <motion.div
                initial={{
                    transform: "translateY(100%)",
                    opacity: 0,
                    pointerEvents: "none",
                }}
                animate={{
                    transform: "translateY(0)",
                    opacity: 1,
                    pointerEvents: "auto",
                }}
                transition={{
                    duration: 0.5,
                    delay: 0.3,
                    ease: "easeInOut",
                }}
                className={styles.bottomRailing}
            />
            <motion.div
                initial={{
                    transform: "translateY(100%)",
                    opacity: 0,
                    pointerEvents: "none",
                }}
                animate={{
                    transform: "translateY(0)",
                    opacity: 1,
                    pointerEvents: "auto",
                }}
                transition={{
                    duration: 0.5,
                    delay: 0.3,
                    ease: "easeInOut",
                }}
                className={styles.tickets}
            >
                <div className={styles.ticketsWrapper}>
                    {Array.from({ length: 2 }).map((_, index) => (
                        <Ticket
                            key={index}
                            index={index + 1}
                            active={activeIndex === index + 1}
                            onClick={() => setActiveIndex(index + 1)}
                        />
                    ))}
                </div>
            </motion.div>
            <Railing position="left" />
            <Railing position="right" />
            <Curtain />
            <div className={styles.shadow} />

            <Nav page={ESubPage.PV} />
            <Exit onClick={onExit} />

            <div
                className={classNames(styles.player, showVideo && styles.show)}
                onClick={handleClosePv}
            >
                {showVideo && activePvIndex !== null && (
                    <iframe
                        width="80%"
                        height="80%"
                        src={t(`pv.${activePvIndex}.src`)}
                        title={t(`pv.${activePvIndex}.title`)}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                    ></iframe>
                )}
            </div>
        </motion.div>
    )
}

export default PagePV
